<div
  class="relative flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
  [class.border-indigo-600]="isPopular"
  [class.border-gray-200]="!isPopular"
  [class.ring-2]="isPopular"
  [class.ring-indigo-600]="isPopular"
>
  <!-- Popular badge -->
  <div
    *ngIf="isPopular"
    class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-1 text-xs font-semibold text-white"
  >
    Most Popular
  </div>

  <!-- Current Plan badge -->
  <div
    *ngIf="isCurrentPlan"
    class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-green-600 px-4 py-1 text-xs font-semibold text-white"
  >
    Current Plan
  </div>

  <!-- Plan name -->
  <h3 class="text-lg font-semibold text-gray-900">{{ plan.displayName }}</h3>
  <p *ngIf="plan.description" class="mt-1 min-h-[2.5rem] text-sm text-gray-500">{{ plan.description }}</p>

  <!-- Price -->
  <div class="mt-4 flex items-baseline">
    <ng-container *ngIf="isFree; else paidPrice">
      <span class="text-4xl font-bold text-gray-900">Free</span>
      <span class="ml-2 text-sm text-gray-500">forever</span>
    </ng-container>
    <ng-template #paidPrice>
      <ng-container *ngIf="matchedPrice; else noPrice">
        <span class="text-4xl font-bold text-gray-900">
          {{ matchedPrice.amount | currency: currencyCode : 'symbol-narrow' : '1.0-0' }}
        </span>
        <span class="ml-2 text-sm text-gray-500">
          / {{ selectedInterval === 'Monthly' ? 'month' : 'year' }}
        </span>
      </ng-container>
      <ng-template #noPrice>
        <span class="text-lg text-gray-400">Contact us</span>
      </ng-template>
    </ng-template>
  </div>

  <!-- Monthly equivalent for yearly -->
  <p
    *ngIf="!isFree && selectedInterval === 'Yearly' && matchedPrice"
    class="mt-1 text-xs text-gray-400"
  >
    {{ matchedPrice.amount / 12 | currency: currencyCode : 'symbol-narrow' : '1.2-2' }} / month
  </p>
  <!-- Spacer to keep monthly line height consistent when not shown -->
  <p
    *ngIf="isFree || selectedInterval === 'Monthly' || !matchedPrice"
    class="mt-1 text-xs"
  >&nbsp;</p>

  <!-- Promo code area — fixed height to keep CTA buttons aligned across cards -->
  <div class="mt-2 h-7">
    <div *ngIf="matchedPromo && !isFree" class="flex items-center gap-1.5 text-xs text-green-700">
      <svg class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
      <span>{{ matchedPromo.percentOff }}% off — use</span>
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded bg-green-50 border border-green-200 px-1.5 py-0.5 font-mono font-semibold text-green-800 hover:bg-green-100 transition-colors cursor-pointer"
        (click)="copyPromoCode($event)"
        [title]="promoCopied ? 'Copied!' : 'Click to copy'"
      >
        {{ matchedPromo.code }}
        <svg *ngIf="!promoCopied" class="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <svg *ngIf="promoCopied" class="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  </div>

  <!-- CTA -->
  <button
    *ngIf="showCta && !isCurrentPlan"
    type="button"
    class="mt-4 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
    [class.bg-indigo-600]="isPopular && !ctaMuted"
    [class.text-white]="isPopular && !ctaMuted"
    [class.hover:bg-indigo-700]="isPopular && !ctaMuted"
    [class.bg-white]="!isPopular && !ctaMuted"
    [class.text-indigo-600]="!isPopular && !ctaMuted"
    [class.border]="!isPopular || ctaMuted"
    [class.border-indigo-600]="!isPopular && !ctaMuted"
    [class.hover:bg-indigo-50]="!isPopular && !ctaMuted"
    [class.bg-gray-50]="ctaMuted"
    [class.text-gray-600]="ctaMuted"
    [class.border-gray-300]="ctaMuted"
    [class.hover:bg-gray-100]="ctaMuted"
    [disabled]="ctaDisabled"
    (click)="onCtaClick()"
  >
    <span *ngIf="!ctaDisabled">{{ ctaLabel }}</span>
    <span *ngIf="ctaDisabled" class="flex items-center justify-center gap-2">
      <div class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
      Loading...
    </span>
  </button>
  <!-- Current plan placeholder button -->
  <button
    *ngIf="showCta && isCurrentPlan"
    type="button"
    class="mt-4 w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-500 cursor-default"
    disabled
  >
    {{ ctaLabel }}
  </button>

  <!-- Divider -->
  <hr class="my-6 border-gray-200" />

  <!-- Features -->
  <div class="space-y-3">
    <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">What's included</p>
    <ul class="space-y-2">
      <li *ngFor="let feature of features" class="flex items-start gap-2 text-sm">
        <ng-container *ngIf="feature.enabled; else featureDisabled">
          <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span class="text-gray-700">{{ feature.label }}</span>
          <span *ngIf="feature.comingSoon" class="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Coming Soon</span>
        </ng-container>
        <ng-template #featureDisabled>
          <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span class="text-gray-400">{{ feature.label }}</span>
          <span *ngIf="feature.comingSoon" class="ml-1 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">Coming Soon</span>
        </ng-template>
      </li>
    </ul>
  </div>

  <!-- Limits -->
  <div *ngIf="limits.length > 0 || isUnlimited" class="pt-4 space-y-3">
    <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Limits</p>

    <!-- Unlimited (Premium) — compact block instead of repetitive list -->
    <div *ngIf="isUnlimited; else limitsList" class="rounded-lg bg-indigo-50 p-4">
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" transform="rotate(180 12 12)" />
        </svg>
        <span class="text-sm font-semibold text-indigo-700">Everything unlimited</span>
      </div>
      <p class="mt-1.5 text-xs text-indigo-600/70">
        No restrictions on tournaments, players, practices, expenses, or any other feature.
      </p>
    </div>

    <!-- Regular limits list (Free / Pro) -->
    <ng-template #limitsList>
      <ul class="space-y-1.5">
        <li *ngFor="let limit of limits" class="flex items-center justify-between text-sm">
          <span class="text-gray-600">{{ limit.label }}</span>
          <span
            class="font-medium"
            [class.text-gray-900]="limit.displayValue !== '—'"
            [class.text-gray-300]="limit.displayValue === '—'"
          >{{ limit.displayValue }}</span>
        </li>
      </ul>
    </ng-template>
  </div>
</div>
